<template>
    <div>
        <div class="container max-w-3xl mx-auto">
            <div class="pt-4 xl:pt-8">
                <div class="md:text-center">
                    <h1 class="max-w-lg mx-auto mb-4 text-3xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-4xl md:text-5xl lg:text-6xl text-grey-darkest">
                        Stay organised
                    </h1>

                    <h2 class="max-w-md mx-auto mb-6 lg:mb-12 md:text-xl xl:text-2xl">
                            There's lots of boring stuff related to renting. Don't worry though, we'll handle it.
                    </h2>
                </div>

                <div class="max-w-sm mx-auto mb-12">
                    <div class="flex flex-col justify-center mb-4 md:flex-row">
                        <laroute-link
                            :to="{
                                name: 'auth.users.create'
                            }"
                            class="flex-auto mb-3 shadow-md md:mb-0 md:mr-3 btn btn-blue btn-xl"
                        >
                            Get started
                        </laroute-link>

                        <laroute-link
                            :to="{
                                name: 'invitations.create'
                            }"
                            class="flex-auto shadow-md btn btn-white btn-xl"
                        >
                            Invite your landlord
                        </laroute-link>
                    </div>

                    <router-link
                        :to="{ name: 'www.landlords.index' }"
                        class="flex items-center font-semibold text-blue"
                    >
                        Are you a landlord?

                        <base-svg
                            name="icon-cheveron-right"
                            class="flex-none w-6 h-6 fill-primary-blue"
                        />
                    </router-link>
                </div>
            </div>
        </div>

        <div class="relative z-10">
            <svg
                class="w-full h-5 text-white xl:h-10"
                preserveAspectRatio="none"
                viewBox="0 0 375 40"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M375 40V0s-87.046 32-187.5 32S0 0 0 0v40h375z" />
            </svg>

            <div class="bg-white">
                <div class="container max-w-2xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center pt-10 lg:flex-row md:pt-16">
                        <grid-layout-item class="lg:w-1/2">
                            <div class="mb-12">
                                <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                                    <base-svg
                                        name="icon-camera"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Shared inventories
                                </h6>

                                <p>
                                    Rentora's inventory tool securely stores photos in high quality - backed up with descriptions.
                                </p>
                            </div>

                            <div>
                                <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                                    <base-svg
                                        name="icon-document-add"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Document storage
                                </h6>

                                <p>
                                    Ditch the shoebox. There's no need to keep paper copies anymore. Documents shared by your landlord
                                    are kept forever in our secure document vault - and are always available.
                                </p>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="lg:w-1/2">
                            <img
                                src="/img/inventory.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '391px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>

                <div
                    class="max-w-4xl mx-auto bg-center bg-no-repeat"
                    :style="{
                        'background-image': 'url(/img/misc-background.svg)',
                        'background-position': 'top 20px left 0',
                        'background-size': '400px 500px'
                    }"
                >
                    <div class="container max-w-2xl mx-auto">
                        <grid-layout class="flex-col-reverse items-center pt-24 lg:flex-row-reverse">
                            <grid-layout-item class="lg:w-1/2">
                                <div class="mb-12">
                                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                                        <base-svg
                                            name="icon-calendar-date"
                                            class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                        />
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Calendar
                                    </h6>

                                    <p class="mb-3">
                                        We maintain a calendar for you, where you can see upcoming rents, tenancy terms, expiring documents and a lot more.
                                    </p>

                                    <p>
                                        You’ll receive reminders in a timely manner too - so you definitely won’t forget.
                                    </p>
                                </div>

                                <div>
                                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                                        <base-svg
                                            name="icon-notification"
                                            class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                        />
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Notifications
                                    </h6>

                                    <p>
                                        You’ll be kept in the loop with everything via email and text. Quite frankly, you’ll rarely need to visit your Rentora dashboard.
                                    </p>
                                </div>
                            </grid-layout-item>
                            <grid-layout-item class="lg:w-1/2">
                                <img
                                    src="/img/calendar.svg"
                                    class="w-full mx-auto"
                                    :style="{
                                        'max-width': '370px'
                                    }"
                                >
                            </grid-layout-item>
                        </grid-layout>
                    </div>
                </div>

                <div
                    class="max-w-4xl mx-auto bg-center bg-no-repeat"
                    :style="{
                        'background-image': 'url(/img/misc-background.svg)',
                        'background-position': 'top 20px right 0',
                        'background-size': '400px 500px'
                    }"
                >
                    <div class="container max-w-2xl mx-auto">
                        <grid-layout class="flex-col-reverse items-center pt-24 lg:flex-row">
                            <grid-layout-item class="lg:w-1/2">
                                <div>
                                    <div class="inline-block p-3 mb-1 rounded-full bg-blue-lightest">
                                        <base-svg
                                            name="icon-chat"
                                            class="w-8 h-8 fill-secondary-blue fill-primary-blue-lighter"
                                        />
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Messaging
                                    </h6>

                                    <p>
                                        Talk to your landlord through our messaging services. Your conversations are recorded forever,
                                        and there's no need to share your phone number.
                                    </p>
                                </div>
                            </grid-layout-item>
                            <grid-layout-item class="lg:w-1/2">
                                <img
                                    src="/img/messaging.svg"
                                    class="w-full mx-auto"
                                    :style="{
                                        'max-width': '402px'
                                    }"
                                >
                            </grid-layout-item>
                        </grid-layout>
                    </div>
                </div>

                <div class="container max-w-2xl pb-24 mx-auto">
                    <div class="w-full my-12 border-b lg:my-24" />

                    <grid-layout class="mb-24">
                        <grid-layout-item class="mb-12 lg:w-1/2 lg:mb-0">
                            <router-link
                                :to="{
                                    name: 'www.tenants.listings.index'
                                }"
                                class="block h-full p-6 rounded shadow-md hover:shadow-lg"
                            >
                                <h2 class="flex items-center mb-3 text-xl font-semibold font-noto-sans text-blue">
                                    Property listings

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="flex-none w-6 h-6 fill-primary-blue"
                                    />
                                </h2>

                                <p class="mb-3">
                                    Find your perfect home. Landlords and agents who use Rentora list their properties with us
                                </p>
                            </router-link>
                        </grid-layout-item>

                        <grid-layout-item class="lg:w-1/2">
                            <router-link
                                :to="{
                                    name: 'www.tenants.payments.index'
                                }"
                                class="block h-full p-6 rounded shadow-md hover:shadow-lg"
                            >
                                <h2 class="flex items-center mb-3 text-xl font-semibold font-noto-sans text-blue">
                                    Paying rent

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="flex-none w-6 h-6 fill-primary-blue"
                                    />
                                </h2>

                                <p class="mb-3">
                                    Pay rent your way - on your terms. We've brought paying rent into the 21st century
                                </p>
                            </router-link>
                        </grid-layout-item>
                    </grid-layout>

                    <div class="flex flex-col items-center max-w-xl p-3 mx-auto rounded lg:flex-row bg-blue-lightest xs:p-6 lg:p-12">
                        <div class="mb-6 lg:mb-0 lg:w-1/2">
                            <h6 class="text-2xl font-semibold md:text-3xl font-noto-sans text-blue-darkest">
                                Ready to get started?
                            </h6>

                            <p class="text-lg lg:text-xl">
                                Create an account in a couple of minutes
                            </p>
                        </div>

                        <div class="flex flex-col justify-center w-full lg:w-1/2 md:flex-row lg:justify-end">
                            <laroute-link
                                :to="{
                                    name: 'auth.users.create'
                                }"
                                class="w-full mb-3 shadow-md md:mb-0 md:mr-3 md:w-auto btn btn-blue-darkest btn-xl"
                            >
                                Get started
                            </laroute-link>

                            <laroute-link
                                :to="{
                                    name: 'invitations.create'
                                }"
                                class="w-full shadow-md md:w-auto btn btn-white btn-xl"
                            >
                                Invite your landlord
                            </laroute-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import HeadMixin from '../../../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        methods: {
            head () {
                return {
                    title: 'Online inventories, document storage and messaging'
                }
            }
        }
    }
</script>
